"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { SchoolLogo } from "@/components/ui/schoolLogo";
import { SCHOOL_NAME } from "@/lib/config";
import { getDashboardPathForRole } from "@/lib/authRedirects";
import GuestRouteGuard from "@/components/auth/GuestRouteGuard";
import {
  AUTH_NOTICE_PASSWORD_CHANGED,
  getAuthNotice,
  PASSWORD_CHANGED_LOGIN_MESSAGE,
  subscribeNoticeCleared,
} from "@/services/api/logoutUtil";
import {
  loginPage,
  loginPageBackground,
} from '@/styles/styles';

type LoginFormInputs = {
  email: string;
  password: string;
};

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();

  const [showPassword, setShowPassword] = useState(false);
  const { login, user, checkAndShowTermsModal } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (getAuthNotice() === AUTH_NOTICE_PASSWORD_CHANGED) {
      setAuthNotice(PASSWORD_CHANGED_LOGIN_MESSAGE);
    }
  }, []);

  useEffect(() => {
    return subscribeNoticeCleared(() => {
      setAuthNotice(null);
    });
  }, []);

  const rawSchool = process.env.NEXT_PUBLIC_SCHOOL || "";
  const showSchoolText = !!rawSchool && rawSchool !== "base" && rawSchool !== "null";

  // Handle redirect after successful login and user data is available
  useEffect(() => {
    if (loginSuccess && user) {
      checkAndShowTermsModal();

      if (user.has_accepted_terms_and_conditions === true) {
        router.replace(getDashboardPathForRole(user.role));
        // Leave isSubmitting true through unmount so the form doesn't flash
        // before navigation completes (GuestRouteGuard stays in children mode).
      } else {
        // Terms modal owns the next step — form can quit submit UX.
        setIsSubmitting(false);
      }

      setLoginSuccess(false);
    }
  }, [loginSuccess, user, router, checkAndShowTermsModal]);

  const onSubmit = async (data: LoginFormInputs) => {
    const { email, password } = data;
    setError(null);
    setIsSubmitting(true);

    const response = await login(email, password);

    if (response.success) {
      setLoginSuccess(true);
    } else {
      setError(response.error ?? null);
      setIsSubmitting(false);
    }
  };

  return (
    <GuestRouteGuard isSubmitting={isSubmitting}>
      <div
        className={loginPage.shell}
        style={loginPageBackground}
      >
        <div className={loginPage.inner}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={loginPage.form}
          >
            {/* Logo */}
            <Link href="/">
              <div className={loginPage.logoWrap}>
                <div className={loginPage.logoInner}>
                  <SchoolLogo
                    width={250}
                    height={250}
                    className={loginPage.logo}
                  />
                </div>
              </div>
            </Link>
            {showSchoolText && (
              <p className={loginPage.schoolName}>
                {SCHOOL_NAME} Learning Analytics
              </p>
            )}

            {/* Title */}
            <h2 className={loginPage.title}>
            Welcome back!
          </h2>
          <p className={loginPage.subtitle}>
          Log in to access quizzes, exams, and learning insights.
          </p>

            {authNotice && (
              <div className={loginPage.notice}>
                <p className={loginPage.noticeText}>{authNotice}</p>
              </div>
            )}

            {/* Email Input */}
            <div className={loginPage.field}>
              <div className={loginPage.fieldHeader}>
                <Label htmlFor="email">Email</Label>
                {errors.email && (
                  <p className={loginPage.fieldError}>{errors.email.message}</p>
                )}
              </div>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email", { required: "*Required" })}
              />
            </div>

            {/* Password Input */}
            <div className={loginPage.field}>
              <div className={loginPage.fieldHeader}>
                <Label htmlFor="password">Password</Label>
                {errors.password && (
                  <p className={loginPage.fieldError}>
                    {errors.password.message}
                  </p>
                )}
              </div>
              <div className={loginPage.passwordWrap}>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  {...register("password", { required: "*Required" })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={loginPage.passwordToggle}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={loginPage.forgotWrap}>
                <Link
                  href="/forgot-password"
                  className={loginPage.forgotLink}
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className={loginPage.submit}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className={loginPage.submitSpinner} />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>

            {/* Error Message */}
            {error && (
              <div className={loginPage.error}>
                <div className={loginPage.errorRow}>
                  <div className={loginPage.errorIconWrap}>
                    <svg className={loginPage.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className={loginPage.errorText}>
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Register link */}
            {/* <div className={loginPage.registerWrap}>
              Don't have an account?{" "}
              <Link href="/register" className={loginPage.registerLink}>
                Register
              </Link>
            </div> */}
          </form>
        </div>
      </div>
    </GuestRouteGuard>
  );
}
